import {hasValue, isEnglishName, validateFullName} from '@sollapay/utils';
import {z} from 'zod';

import type {StringFieldRule, Translate} from './id-field-rules';
import type {FullNameError} from '@sollapay/utils';

/** Shared by `fullNameRule` and any Zod `superRefine` that needs the same i18n keys. */
export const FULL_NAME_ERROR_KEYS: Record<FullNameError, string> = {
  missing_last_name: 'common:fields.fullName.invalid_missing_last_name'
};

/** Required + "first and last name" format. Shared by buyer/signatory name fields. */
export function fullNameRule(t: Translate): StringFieldRule {
  return {
    required: t('common:fields.fullName.required'),
    validate: val => {
      if (!val) return true; // `required` already covers emptiness
      const error = validateFullName(val);
      return !error || t(FULL_NAME_ERROR_KEYS[error]);
    }
  };
}

/** Required + English letters only; whitespace-only input counts as missing. */
export function englishNameRule(t: Translate, requiredMessage: string): StringFieldRule {
  return {
    required: requiredMessage,
    validate: val => {
      if (!hasValue(val)) return requiredMessage;
      return isEnglishName(val) || t('common:fields.englishName.invalid');
    }
  };
}

/** Zod `superRefine` counterpart of `fullNameRule` — a blank value is left to the `required` check. */
export function addFullNameIssue(ctx: z.RefinementCtx, value: string, path = 'fullName'): void {
  const error = value ? validateFullName(value) : null;
  if (error) {
    ctx.addIssue({code: z.ZodIssueCode.custom, path: [path], message: FULL_NAME_ERROR_KEYS[error]});
  }
}
